import { CloseOutlined, DownOutlined, PlusOutlined } from '@ant-design/icons';
import { Dropdown } from 'antd';
import { Link } from 'react-router';

import { ShipmentClassification } from '@invenco/shipping-interface';

import { Button } from '@/components-ant/button';
import { Card } from '@/components-ant/card';
import { DetailList } from '@/components-ant/detail-list/DetailList';
import { DetailsBlock } from '@/components-ant/details-block';
import { DetailsCard } from '@/components-ant/details-card';
import { Header } from '@/components-ant/header';
import { LoadingSkeleton } from '@/components-ant/loading-skeleton';

import { formatDate } from '../../../../shared/helpers';
import { shipmentClassificationTitle } from '../../../../shared/title-maps';
import {
  Container,
  DetailContainer,
  MainContainer,
  SideContainer,
} from '../../../../styles/layout';
import { CarrierSelectionModal } from './carrier-selection-modal/CarrierSelectionModal';
import { DetailsModal } from './details-modal/DetailsModal';
import { PriceBands } from './price-bands/PriceBands';
import { ActionButton } from './price-bands/styles';
import { BottomContainer } from './styles';
import { useShippingPriceDetailsPage } from './useShippingPriceDetailsPage';

export function ShippingPriceDetails() {
  const {
    models: {
      isNew,
      isSaving,
      isLoading,
      isLoadingAccounts,
      isLoadingCarriers,
      isDetailsModalOpen,
      hasChanges,
      canSave,
      shippingPrice,
      isCarrierSelectionModalOpen,
      accounts,
      account,
      location,
      carrierOptions,
      breadcrumbs,
      availableClassifications,
      existingCarrierClassifications,
    },
    operations: {
      refresh,
      updateDetails,
      save,
      addNewGroup,
      removeGroup,
      addBand,
      addGroupBand,
      updateBand,
      updateGroupBand,
      removeBand,
      removeGroupBand,
      addClassifiedGroup,
      removeClassifiedGroup,
      addClassifiedBand,
      updateClassifiedBand,
      removeClassifiedBand,
      openDetailsModal,
      closeDetailsModal,
      openCarrierSelectionModal,
      closeCarrierSelectionModal,
    },
  } = useShippingPriceDetailsPage();

  return (
    <Container>
      <Header
        title={shippingPrice.name}
        documentTitle={isNew ? 'New Shipping Price' : shippingPrice.name}
        backURL="/shipping/prices"
        links={breadcrumbs}
        loading={isLoading}
        onRefresh={!isNew ? refresh : undefined}
        actionContent={
          <Button
            type="primary"
            onClick={() => void save()}
            disabled={!canSave || !hasChanges}
            loading={isSaving}
          >
            Save
          </Button>
        }
        editable
        editInitially={isNew}
        editPlaceholder="Enter shipping price name..."
        editAriaLabel="Shipping price name"
        onSave={(name) => updateDetails({ name })}
      />

      <DetailContainer>
        <MainContainer>
          <Card
            title="Price bands"
            count={shippingPrice.priceBands?.length ?? 0}
            description={
              "Each band's markup is applied in sequence as an additional percentage of the shipping cost. " +
              "The markup is applied to the portion of the cost between the previous band's (if any) maximum " +
              "and this band's maximum."
            }
          >
            <PriceBands
              loading={isLoading}
              priceBands={shippingPrice.priceBands ?? []}
              addBand={addBand}
              updateBand={updateBand}
              removeBand={removeBand}
            />
          </Card>
          {shippingPrice.classifiedPriceBands?.map((classified, classifiedIndex) => (
            <Card
              key={classified.classification}
              title={`${shipmentClassificationTitle[classified.classification]} price bands`}
              count={classified.priceBands?.length ?? 0}
              rightContent={
                <ActionButton
                  type="text"
                  danger
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={() => removeClassifiedGroup(classifiedIndex)}
                  aria-label="Delete"
                />
              }
            >
              <PriceBands
                loading={isLoading}
                priceBands={classified.priceBands ?? []}
                addBand={(band) => addClassifiedBand(classifiedIndex, band)}
                updateBand={(bandIndex, band) =>
                  updateClassifiedBand(classifiedIndex, bandIndex, band)
                }
                removeBand={(bandIndex) => removeClassifiedBand(classifiedIndex, bandIndex)}
              />
            </Card>
          ))}
          {shippingPrice.carrierPrices?.map((carrierShippingPrice, groupIndex) => (
            <Card
              key={`${carrierShippingPrice.carrierId}-${carrierShippingPrice.classification ?? 'unclassified'}`}
              title={`${carrierShippingPrice.carrierName}${
                carrierShippingPrice.classification
                  ? ` (${shipmentClassificationTitle[carrierShippingPrice.classification]})`
                  : ''
              } price bands`}
              count={carrierShippingPrice.priceBands?.length ?? 0}
              rightContent={
                <ActionButton
                  type="text"
                  danger
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={() => removeGroup(groupIndex)}
                  aria-label="Delete"
                />
              }
            >
              <PriceBands
                loading={isLoading}
                priceBands={carrierShippingPrice.priceBands ?? []}
                addBand={(band) => addGroupBand(groupIndex, band)}
                updateBand={(bandIndex, band) => updateGroupBand(groupIndex, bandIndex, band)}
                removeBand={(bandIndex) => removeGroupBand(groupIndex, bandIndex)}
              />
            </Card>
          ))}
          <BottomContainer>
            {availableClassifications.length > 0 && (
              <Dropdown
                menu={{
                  items: availableClassifications.map((c) => ({
                    key: c,
                    label: shipmentClassificationTitle[c],
                  })),
                  onClick: ({ key }) => addClassifiedGroup(key as ShipmentClassification),
                }}
              >
                <Button inline type="link" icon={<PlusOutlined />}>
                  Add classified price bands <DownOutlined />
                </Button>
              </Dropdown>
            )}
            <Button inline type="link" icon={<PlusOutlined />} onClick={openCarrierSelectionModal}>
              Add carrier band group
            </Button>
          </BottomContainer>
        </MainContainer>

        <SideContainer>
          <DetailsCard title="Details" hasEditButton onEdit={openDetailsModal}>
            <DetailsBlock loading={isLoading} loadingRows={3}>
              <DetailList
                items={[
                  {
                    label: 'Account',
                    value: isLoadingAccounts ? (
                      <LoadingSkeleton />
                    ) : (
                      account && <Link to={`/accounts/${account.id}`}>{account.name}</Link>
                    ),
                  },
                  {
                    label: 'Valid From',
                    value: shippingPrice.validFrom && formatDate(shippingPrice.validFrom),
                  },
                  {
                    label: 'Valid To',
                    value: shippingPrice.validTo && formatDate(shippingPrice.validTo),
                  },
                  {
                    label: 'Location',
                    value: isLoadingAccounts ? <LoadingSkeleton /> : location?.name,
                  },
                ]}
              />
            </DetailsBlock>
          </DetailsCard>
        </SideContainer>
      </DetailContainer>

      <DetailsModal
        isOpen={isDetailsModalOpen}
        isNew={isNew}
        shippingPrice={shippingPrice}
        accounts={accounts}
        updateDetails={updateDetails}
        onClose={closeDetailsModal}
      />

      <CarrierSelectionModal
        isOpen={isCarrierSelectionModalOpen}
        loading={isLoadingCarriers}
        carriers={carrierOptions}
        existingCarrierClassifications={existingCarrierClassifications}
        onSave={addNewGroup}
        onClose={closeCarrierSelectionModal}
      />
    </Container>
  );
}
